import React, { useEffect, useState } from 'react';
import { notificationApi, getAuthSession } from '../services/api';

function TherapistNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const authSession = getAuthSession();

  // Fetch notifications - Load only once on mount
  useEffect(() => {
    if (!authSession?.user || authSession.user.role !== 'physiotherapist') {
      return;
    }

    const loadNotifications = async () => {
      try {
        setIsLoading(true);
        const response = await notificationApi.getAll({ limit: 10 });
        setNotifications(response.notifications || []);
        setUnreadCount(response.unreadCount || 0);
      } catch (error) {
        console.error('Error loading notifications:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadNotifications();
    
    // Poll for new notifications every 30 seconds
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, []); // Empty dependency - load only once on mount

  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationApi.markAsRead(notificationId);
      setNotifications(prev =>
        prev.map(n => n._id === notificationId ? { ...n, isRead: true } : n)
      );
      setUnreadCount(Math.max(0, unreadCount - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationApi.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      await notificationApi.delete(notificationId);
      const deletedNotification = notifications.find(n => n._id === notificationId);
      setNotifications(prev => prev.filter(n => n._id !== notificationId));
      if (!deletedNotification?.isRead) {
        setUnreadCount(Math.max(0, unreadCount - 1));
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const formatDate = (date) => {
    const now = new Date();
    const notifDate = new Date(date);
    const diffMs = now - notifDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return notifDate.toLocaleDateString();
  };

  return (
    <div style={{ position: 'relative' }}>
      {/* Notification Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          background: 'none',
          border: 'none',
          fontSize: '20px',
          cursor: 'pointer',
          position: 'relative',
          color: '#667eea',
          transition: 'transform 0.2s ease',
          transform: isOpen ? 'scale(1.1)' : 'scale(1)',
        }}
        title="Notifications"
      >
        🔔
        {unreadCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: '-8px',
              right: '-8px',
              background: '#ef4444',
              color: 'white',
              borderRadius: '50%',
              width: '20px',
              height: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              fontWeight: 'bold',
            }}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notifications Dropdown Panel */}
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '40px',
            right: '0',
            width: '380px',
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            maxHeight: '500px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '16px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '12px 12px 0 0',
              color: 'white',
            }}
          >
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>
              🔔 Notifications
            </h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  color: 'white',
                  padding: '4px 12px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  cursor: 'pointer',
                  transition: 'background 0.2s ease',
                }}
                onMouseEnter={e => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
                onMouseLeave={e => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div
            style={{
              overflowY: 'auto',
              flex: 1,
              maxHeight: '400px',
            }}
          >
            {isLoading ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af' }}>
                Loading notifications...
              </div>
            ) : notifications.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af' }}>
                No notifications yet
              </div>
            ) : (
              notifications.map(notification => (
                <div
                  key={notification._id}
                  style={{
                    padding: '16px',
                    borderBottom: '1px solid #f3f4f6',
                    background: notification.isRead ? '#fff' : '#f0f7ff',
                    transition: 'background 0.2s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      background: '#f9fafb',
                    },
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                  onMouseLeave={e => e.currentTarget.style.background = notification.isRead ? '#fff' : '#f0f7ff'}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '8px',
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          marginBottom: '4px',
                        }}
                      >
                        <h4
                          style={{
                            margin: 0,
                            fontSize: '14px',
                            fontWeight: 'bold',
                            color: '#1f2937',
                          }}
                        >
                          {notification.title}
                        </h4>
                        {!notification.isRead && (
                          <div
                            style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              background: '#3b82f6',
                            }}
                          />
                        )}
                      </div>
                      <p
                        style={{
                          margin: '4px 0',
                          fontSize: '13px',
                          color: '#4b5563',
                          lineHeight: '1.4',
                        }}
                      >
                        {notification.message}
                      </p>
                      {notification.clientName && (
                        <p style={{ margin: '4px 0', fontSize: '12px', color: '#9ca3af' }}>
                          👤 {notification.clientName} ({notification.clientEmail})
                        </p>
                      )}
                      <p style={{ margin: '4px 0', fontSize: '12px', color: '#9ca3af' }}>
                        📅 {new Date(notification.sessionDate).toLocaleDateString()} | {new Date(notification.sessionDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                      <p style={{ margin: '4px 0', fontSize: '12px', color: '#9ca3af' }}>
                        {notification.sessionMode === 'online' ? '💻 Online' : '🏥 In-Person'}
                      </p>
                      <p
                        style={{
                          margin: '8px 0 0 0',
                          fontSize: '11px',
                          color: '#d1d5db',
                        }}
                      >
                        {formatDate(notification.createdAt)}
                      </p>
                    </div>
                    <div style={{ marginLeft: '8px', display: 'flex', gap: '4px' }}>
                      {!notification.isRead && (
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            handleMarkAsRead(notification._id);
                          }}
                          style={{
                            background: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            cursor: 'pointer',
                            transition: 'background 0.2s ease',
                          }}
                          onMouseEnter={e => e.target.style.background = '#2563eb'}
                          onMouseLeave={e => e.target.style.background = '#3b82f6'}
                          title="Mark as read"
                        >
                          ✓
                        </button>
                      )}
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          handleDelete(notification._id);
                        }}
                        style={{
                          background: '#ef4444',
                          color: 'white',
                          border: 'none',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          transition: 'background 0.2s ease',
                        }}
                        onMouseEnter={e => e.target.style.background = '#dc2626'}
                        onMouseLeave={e => e.target.style.background = '#ef4444'}
                        title="Delete notification"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div
              style={{
                padding: '12px',
                borderTop: '1px solid #e5e7eb',
                textAlign: 'center',
                background: '#f9fafb',
                borderRadius: '0 0 12px 12px',
              }}
            >
              <button
                onClick={() => setIsOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#667eea',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '500',
                  textDecoration: 'none',
                }}
              >
                View all notifications
              </button>
            </div>
          )}
        </div>
      )}

      {/* Click outside to close */}
      {isOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999,
          }}
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
}

export default TherapistNotifications;
